import { useCallback, useEffect, useRef, useState } from 'react';
import type { AssetDetail } from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { fetchAssetDetail } from './viewer-api';

/**
 * Charge le détail d'un asset pour la visionneuse et le garde localement afin de
 * refléter immédiatement les mutations (état, notes) sans re-fetch complet.
 *
 * `assetId === null` ⇒ inerte (aucune requête) : permet de monter le hook avant
 * qu'un média soit sélectionné — et surtout de ne RIEN charger tant qu'aucun
 * consommateur n'est ouvert (audit A2 : feuilleter tous panneaux fermés ne doit
 * coûter aucune requête de détail). Un changement d'`assetId` annule le chargement
 * en vol et repart de zéro.
 *
 * GRAINE (`seed`, audit A1) — détail DÉJÀ chargé par un autre appelant pour ce même
 * asset (`useColdAsset` en accès à froid / mode série / diaporama). Quand elle
 * correspond à `assetId`, le hook démarre `ready` sur cette valeur et N'ÉMET AUCUNE
 * requête : le `GET /api/assets/:id` n'est plus fait deux fois par photo. La graine
 * n'est lue qu'à l'ARRIVÉE d'un asset ; les mutations locales (`setDetail`) et le
 * `reload` explicite gardent la main ensuite.
 */

export type DetailStatus = 'idle' | 'loading' | 'error' | 'ready';

export interface UseAssetDetailResult {
  status: DetailStatus;
  detail: AssetDetail | null;
  errorCode: string | null;
  reload: () => void;
  /** Remplace le détail courant (mutation locale optimiste après une écriture). */
  setDetail: (next: AssetDetail) => void;
}

export function useAssetDetail(
  assetId: string | null,
  seed: AssetDetail | null = null,
): UseAssetDetailResult {
  const [status, setStatus] = useState<DetailStatus>('idle');
  const [detail, setDetailState] = useState<AssetDetail | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  // Graine lue via une ref : sa seule fonction est d'ÉVITER le fetch au moment où
  // l'effet se déclenche. En dépendance d'effet, elle relancerait tout le cycle à
  // chaque fois que l'appelant re-rend avec un nouvel objet.
  const seedRef = useRef(seed);
  seedRef.current = seed;
  // Détail courant lu de façon synchrone par l'effet, sans en faire une dépendance
  // (il en est le produit → boucle immédiate).
  const detailRef = useRef(detail);
  detailRef.current = detail;

  const load = useCallback((id: string) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setStatus('loading');
    setErrorCode(null);

    fetchAssetDetail(id, controller.signal)
      .then((d) => {
        if (controller.signal.aborted) return;
        setDetailState(d);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setErrorCode(err instanceof ApiError ? err.code : 'NETWORK');
        setStatus('error');
      });
  }, []);

  useEffect(() => {
    if (assetId === null) {
      controllerRef.current?.abort();
      setStatus('idle');
      setErrorCode(null);
      // Le détail RETENU n'est PAS effacé : refermer puis rouvrir le panneau « Détail »
      // sur la même photo ne doit pas re-payer une requête (le hook redevient `idle`,
      // donc aucun consommateur ne l'affiche entre-temps). L'égalité d'id ci-dessous
      // garantit qu'un détail retenu ne peut jamais être servi pour une AUTRE photo.
      return;
    }
    // Détail du MÊME asset déjà en main — soit redescendu par l'appelant qui vient de le
    // charger à froid (graine, audit A1), soit retenu d'une ouverture précédente du
    // panneau. Dans les deux cas : aucune requête (audit A2).
    const known = [seedRef.current, detailRef.current].find(
      (candidate) => candidate !== null && candidate.id === assetId,
    );
    if (known) {
      controllerRef.current?.abort();
      setDetailState(known);
      setErrorCode(null);
      setStatus('ready');
      return;
    }
    load(assetId);
    return () => {
      controllerRef.current?.abort();
    };
  }, [assetId, load]);

  const reload = useCallback(() => {
    if (assetId !== null) load(assetId);
  }, [assetId, load]);

  const setDetail = useCallback((next: AssetDetail) => {
    setDetailState(next);
  }, []);

  return { status, detail, errorCode, reload, setDetail };
}
